import { I18n } from '@calendly/i18n'
import { Avatar } from '@calendly/ui/components/avatar'
import { FullStoryExclude } from '@calendly/ui/components/fullstory-privacy'
import { BodyText, LabelText } from '@calendly/ui/components/typography'
import { colorTextSecondary } from '@calendly/ui/theme'

import { RECAP_RELATIONS } from 'features/recaps/constants'
import { useRecapShare } from 'features/recaps/meeting_list/meeting_card/recap_share_context'

import { USER_ACCESS_LIST_VARIANT } from './constants'
import {
  ExternalViewerPermissionSelect,
  PermissionSelectReadOnly,
  RecipientPermissionSelect,
} from './permission_select'
import {
  AccessListItem,
  AccessListItemContent,
  AccessListItemLeading,
  AccessListItemText,
  accessListItemPrimaryLineCss,
  accessListItemPrimaryTextCss,
  accessListItemTextLineCss,
} from './styles'

const isExternalVariant = (variant) => (
  variant === USER_ACCESS_LIST_VARIANT.EXTERNAL
  || variant === USER_ACCESS_LIST_VARIANT.PENDING_INVITE
)

const getDisplayName = (user, variant) => {
  if (variant === USER_ACCESS_LIST_VARIANT.CURRENT_SHARER) {
    return I18n.t('publisher.meeting_recap.share_modal.access_list.current_user_name', {
      name: user.name,
    })
  }

  if (variant === USER_ACCESS_LIST_VARIANT.OWNER && user.isCurrentUser) {
    return I18n.t('publisher.meeting_recap.share_modal.access_list.owner_name', {
      name: user.name,
    })
  }

  if (isExternalVariant(variant)) {
    return user.name || user.email
  }

  return user.name
}

const ExternalViewerLabel = () => (
  <>
    <LabelText size="medium" color={colorTextSecondary}>·</LabelText>
    <BodyText size="small" color="muted">
      {I18n.t('publisher.meeting_recap.share_modal.access_list.external_viewer')}
    </BodyText>
  </>
)

export const UserAccessListItem = ({
  user,
  testId,
  variant = USER_ACCESS_LIST_VARIANT.GRANT,
}) => {
  const {
    permissionUpdates: {
      getUserRelation,
      getUserEmailRelation,
      setPendingUserPermission,
      setPendingUserEmailPermission,
    },
  } = useRecapShare()

  const displayName = getDisplayName(user, variant)
  const avatarLabel = user.name || user.email
  const relation = isExternalVariant(variant)
    ? getUserEmailRelation(user.email, user.relation)
    : getUserRelation(user.uuid, user.relation)

  const renderAction = () => {
    if (variant === USER_ACCESS_LIST_VARIANT.OWNER) {
      return (
        <PermissionSelectReadOnly
          label={I18n.t('publisher.meeting_recap.share_modal.access_list.permission_select.owner')}
          testId={`${testId}-permission`}
        />
      )
    }

    if (variant === USER_ACCESS_LIST_VARIANT.CURRENT_SHARER) {
      return (
        <PermissionSelectReadOnly
          label={I18n.t('publisher.meeting_recap.share_modal.access_list.permission_select.can_view_and_share')}
          testId={`${testId}-permission`}
        />
      )
    }

    if (variant === USER_ACCESS_LIST_VARIANT.EXTERNAL) {
      return (
        <ExternalViewerPermissionSelect
          testIdKey={user.uuid}
          value={relation ?? RECAP_RELATIONS.EXTERNAL_VIEWER}
          onChange={(selectedRelation) => setPendingUserEmailPermission(user.email, selectedRelation)}
          onRemove={() => setPendingUserEmailPermission(user.email, null)}
        />
      )
    }

    if (variant === USER_ACCESS_LIST_VARIANT.PENDING_INVITE) {
      return (
        <ExternalViewerPermissionSelect
          testIdKey={`pending-${user.email}`}
          value={relation ?? RECAP_RELATIONS.EXTERNAL_VIEWER}
          onChange={(selectedRelation) => setPendingUserEmailPermission(user.email, selectedRelation)}
          onRemove={() => setPendingUserEmailPermission(user.email, null)}
        />
      )
    }

    return (
      <RecipientPermissionSelect
        uuid={user.uuid}
        value={relation ?? RECAP_RELATIONS.VIEWER}
        onChange={(selectedRelation) => setPendingUserPermission(user.uuid, selectedRelation)}
        onRemove={() => setPendingUserPermission(user.uuid, null)}
      />
    )
  }

  return (
    <AccessListItem data-testid={testId}>
      <AccessListItemContent>
        <AccessListItemLeading>
          <FullStoryExclude>
            <Avatar
              size="small"
              src={user.avatarUrl}
              letter={user.avatarLetter ?? undefined}
              aria-label={avatarLabel}
              aria-hidden
              tabIndex={-1}
            />
          </FullStoryExclude>
        </AccessListItemLeading>
        <AccessListItemText>
          {isExternalVariant(variant) ? (
            <div className={accessListItemPrimaryLineCss}>
              <BodyText className={accessListItemPrimaryTextCss}>{displayName}</BodyText>
              <ExternalViewerLabel />
            </div>
          ) : (
            <BodyText className={accessListItemTextLineCss}>{displayName}</BodyText>
          )}
          {user.email ? (
            <BodyText className={accessListItemTextLineCss} color="muted" size="small">{user.email}</BodyText>
          ) : null}
        </AccessListItemText>
      </AccessListItemContent>
      {renderAction()}
    </AccessListItem>
  )
}
